import { z } from "zod";

import {
  designDirectionRequestSchema,
  designDirectionSetSchema,
} from "../../designDirection";

export const mediaMcpReferenceAssetSchema = z
  .object({
    r2Uri: z.string().optional(),
    url: z.url().optional(),
    base64Data: z.string().optional(),
    mimeType: z.string().optional(),
  })
  .refine((value) => {
    return Boolean(value.r2Uri ?? value.url ?? value.base64Data);
  }, "Each reference asset must include one source field");

export const mediaMcpImageModelSchema = z
  .enum(["gemini-3.1-flash-image", "gemini-3.1-flash-image-preview"])
  .describe("MediaMcpImageModel");

// Passed through to models that support it (OpenRouter).
export const mediaMcpImageQualitySchema = z
  .enum(["low", "medium", "high"])
  .describe("MediaMcpImageQuality");

const mediaMcpImageSizeSchema = z
  .string()
  .regex(/^\d+x\d+$/, "size must be WIDTHxHEIGHT, e.g. 1280x2560");

// Where a generated image lands. "chatFile" (default) is a private, signed,
// project-scoped file hidden from the Assets and Files apps — the target for
// review-then-choose creative work, so generations don't auto-clutter Assets.
// "asset" is a durable public asset on the CDN with a stable URL, required for
// page-ready imagery that gets referenced in code or a published page.
export const mediaMcpImagePersistTargetSchema = z
  .enum(["asset", "chatFile"])
  .describe("MediaMcpImagePersistTarget");

export const mediaMcpGenerateImageRequestSchema = z
  .object({
    prompt: z.string().min(1),
    references: z.array(mediaMcpReferenceAssetSchema).optional(),
    assetName: z.string().min(1).optional(),
    outputFileExtension: z.string().optional(),
    model: mediaMcpImageModelSchema.optional(),
    size: mediaMcpImageSizeSchema.optional(),
    quality: mediaMcpImageQualitySchema.optional(),
    persistAs: mediaMcpImagePersistTargetSchema.optional(),
  })
  .describe("MediaMcpGenerateImageRequest");

export const mediaMcpGenerateImageInputSchema = z
  .object({
    prompt: z.string().min(1).optional(),
    references: z.array(mediaMcpReferenceAssetSchema).optional(),
    assetName: z.string().min(1).optional(),
    outputFileExtension: z.string().optional(),
    model: mediaMcpImageModelSchema.optional(),
    size: mediaMcpImageSizeSchema.optional(),
    quality: mediaMcpImageQualitySchema.optional(),
    persistAs: mediaMcpImagePersistTargetSchema.optional(),
    requests: z
      .array(mediaMcpGenerateImageRequestSchema)
      .min(1)
      .max(10)
      .optional(),
  })
  .superRefine((value, context) => {
    const hasSingleRequest = Boolean(value.prompt);
    const hasBatchRequests = Boolean(value.requests);

    if (!hasSingleRequest && !hasBatchRequests) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide either prompt or requests",
      });
    }

    if (hasSingleRequest && hasBatchRequests) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide prompt for single request or requests for batch",
      });
    }
  });

export const mediaMcpGenerateVideoInputSchema = z.object({
  prompt: z.string().min(1),
  references: z.array(mediaMcpReferenceAssetSchema).optional(),
  assetName: z.string().min(1).optional(),
  aspectRatio: z.string().optional(),
  durationSeconds: z.number().int().min(1).max(30).optional(),
});

export const mediaMcpGetGenerationStatusInputSchema = z.object({
  jobIds: z.array(z.string().min(1)).min(1).max(10),
});

export const mediaMcpGeneratedAssetOutputSchema = z
  .object({
    mimeType: z.string(),
    model: z.string(),
    sizeBytes: z.number(),
    url: z.string(),
  })
  .describe("MediaMcpGeneratedAssetOutput");

export const mediaMcpGenerationJobKindSchema = z.enum(["image", "video"]);

export const mediaMcpGenerationJobOutputSchema = z
  .object({
    asset: mediaMcpGeneratedAssetOutputSchema.optional(),
    assetName: z.string().optional(),
    createdAt: z.string(),
    error: z.string().optional(),
    estimatedDurationSeconds: z.number().optional(),
    estimatedSecondsRemaining: z.number().optional(),
    jobId: z.string(),
    kind: mediaMcpGenerationJobKindSchema,
    model: z.string(),
    progressPercent: z.number().optional(),
    status: z.enum(["queued", "in_progress", "completed", "failed"]),
    statusDetail: z.string().optional(),
    updatedAt: z.string(),
  })
  .describe("MediaMcpGenerationJobOutput");

export const mediaMcpGenerateJobOutputSchema = mediaMcpGenerationJobOutputSchema
  .partial()
  .extend({
    jobIds: z.array(z.string()).optional(),
    nextAction: z.string(),
    results: z.array(mediaMcpGenerationJobOutputSchema).optional(),
    success: z.boolean(),
    total: z.number().optional(),
  });

export const mediaMcpGetGenerationStatusOutputSchema = z.object({
  failureCount: z.number(),
  results: z.array(
    mediaMcpGenerationJobOutputSchema.extend({
      success: z.boolean(),
    }),
  ),
  success: z.boolean(),
  successCount: z.number(),
  total: z.number(),
});

export const mediaMcpDesignDirectionSetOutputSchema = designDirectionSetSchema
  .extend({
    estimatedDurationSeconds: z.number().optional(),
    nextAction: z.string(),
    success: z.boolean(),
  })
  .describe("MediaMcpDesignDirectionSetOutput");

export const knownMediaMcpToolMetadataRegistrySchema = z
  .object({
    generate_design_directions: z.object({
      iconKey: z.literal("mediaMcp"),
      input: designDirectionRequestSchema,
      output: mediaMcpDesignDirectionSetOutputSchema,
    }),
    generate_image: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGenerateImageInputSchema,
      output: mediaMcpGenerateJobOutputSchema,
    }),
    generate_video: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGenerateVideoInputSchema,
      output: mediaMcpGenerateJobOutputSchema,
    }),
    get_generation_status: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGetGenerationStatusInputSchema,
      output: mediaMcpGetGenerationStatusOutputSchema,
    }),
  })
  .describe("KnownMediaMcpToolMetadataRegistry");
